import { useTranslation } from 'react-i18next';
import { DataTableColumns } from '@tq/ui-components';

import { createColumnItem, createColumnItemDate } from '../utils';
import { ReportData } from 'types';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

type useReportColumnsProps = {
  applicationReference: DataTableColumns<ReportData>;
  authority: DataTableColumns<ReportData>;
  category: DataTableColumns<ReportData>;
  consultationRequestDate: DataTableColumns<ReportData>;
  consultationStatus: DataTableColumns<ReportData>;
  consultationType: DataTableColumns<ReportData>;
  consulteeResponseDueDate: DataTableColumns<ReportData>;
  dateConsultationRespondedTo: DataTableColumns<ReportData>;
  extensionRequestDate: DataTableColumns<ReportData>;
  extensionRequestReason: DataTableColumns<ReportData>;
  extensionRequestResponse: DataTableColumns<ReportData>;
  extensionResponseDate: DataTableColumns<ReportData>;
  lateResponseReason: DataTableColumns<ReportData>;
  otherReason: DataTableColumns<ReportData>;
  responseType: DataTableColumns<ReportData>;
};
export const useReportColumns = (): useReportColumnsProps => {
  const { t } = useTranslation('reports');
  const formatLocaleDate = useFormatLocaleDate();

  const authority = createColumnItem('authority', t('authority', 'Authority'));
  const applicationReference = createColumnItem(
    'applicationReference',
    t('application_reference', 'Application reference')
  );
  const category = createColumnItem('category', t('category', 'Category'));
  const consultationRequestDate = createColumnItemDate(
    'consultationRequestDate',
    t('consultation_request_date', 'Consultation request date'),
    formatLocaleDate
  );
  const consultationStatus = createColumnItem(
    'consultationStatus',
    t('consultation_status', 'Consultation status')
  );
  const consultationType = createColumnItem(
    'consultationType',
    t('consultation_type', 'Consultation type')
  );
  const consulteeResponseDueDate = createColumnItemDate(
    'consulteeResponseDueDate',
    t('consultee_response_due_date', 'Consultee response due date'),
    formatLocaleDate
  );
  const dateConsultationRespondedTo = createColumnItemDate(
    'dateConsultationRespondedTo',
    t('date_consultation_responded_to', 'Date consultation responded to'),
    formatLocaleDate
  );
  const extensionRequestDate = createColumnItemDate(
    'extensionRequestDate',
    t('extension_request_date', 'Date extension requested'),
    formatLocaleDate
  );
  const extensionRequestReason = createColumnItem(
    'extensionRequestReason',
    t('extension_request_reason', 'Reason for extension')
  );
  const extensionRequestResponse = createColumnItem(
    'extensionRequestResponse',
    t('extension_request_response', 'Extension approved / refused')
  );
  const extensionResponseDate = createColumnItemDate(
    'extensionResponseDate',
    t('extension_response_date', 'Date extension approved / refused'),
    formatLocaleDate
  );

  const lateResponseReason = createColumnItem(
    'lateResponseReason',
    t('late_response_reason', 'Late response reason')
  );

  const otherReason = createColumnItem('otherReason', t('other_reason', 'Other reason'));

  const responseType = createColumnItem('responseType', t('response_type', 'Response type'));

  return {
    applicationReference,
    authority,
    category,
    consultationRequestDate,
    consultationStatus,
    consultationType,
    consulteeResponseDueDate,
    dateConsultationRespondedTo,
    extensionRequestDate,
    extensionRequestReason,
    extensionRequestResponse,
    extensionResponseDate,
    lateResponseReason,
    otherReason,
    responseType,
  };
};
